import React, { Component } from 'react';
import 'react-dates/initialize';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { DateRangePicker } from 'react-dates';
import {
  ComposedChart,
  Bar,
  Line,
  YAxis,
  XAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';

import { fetchTransactionGraph } from 'modules/transactionGraph';
import LoadingOverlay from 'components/LoadingOverlay';

class TransactionGraph extends Component {
  dateFormat = 'YYYY-MM-DD';

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isLoaded: PropTypes.bool.isRequired,
    transactions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
    match: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      startDate: moment().startOf('isoWeek'),
      endDate: moment().endOf('isoWeek'),
      focusedInput: null,
    };
  }

  componentDidMount() {
    const { isLoading, dispatch } = this.props;
    const { startDate, endDate } = this.state;

    if (isLoading === false) {
      dispatch(
        fetchTransactionGraph({
          from: startDate.format(this.dateFormat),
          to: endDate.format(this.dateFormat),
        }),
      );
    }
  }

  /**
   * Fetch the data when startDate and endDate changed
   */
  componentDidUpdate(prevProps, prevState) {
    const { startDate, endDate } = this.state;
    const { isLoading, dispatch } = this.props;
    let needFetching = false;

    if (startDate && endDate) {
      if (prevState.startDate === null) {
        needFetching = true;
      } else {
        needFetching = startDate.isSame(prevState.startDate) === false;
      }

      if (prevState.endDate === null) {
        needFetching = true;
      } else {
        needFetching = endDate.isSame(prevState.endDate) === false;
      }

      if (needFetching && isLoading === false) {
        needFetching = false;
        dispatch(
          fetchTransactionGraph({
            from: startDate.format(this.dateFormat),
            to: endDate.format(this.dateFormat),
          }),
        );
      }
    }
  }

  /**
   * Event when range preset changed
   */
  onRangePresetChanged = (rangePreset) => {
    let startDate;
    let endDate;

    switch (rangePreset) {
      case 'week':
        startDate = moment().startOf('isoWeek');
        endDate = moment().endOf('isoWeek');
        break;
      case 'month':
        startDate = moment().startOf('month');
        endDate = moment().endOf('month');
        break;
      default:
        startDate = null;
        endDate = null;
        break;
    }

    this.setState({ startDate, endDate });
  };

  /**
   * Get the date between startDate and endDate
   * @param moment startDate
   * @param moment endDate
   */
  static getDateRange(startDate, endDate) {
    const days = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day.toDate());
      day = day.clone().add(1, 'd');
    }

    return days;
  }

  /**
   * Check if day inside the calendar can be selected
   * the calendar range is between today and last 3 months
   */
  isDayDisabled = (day) => {
    const lastThreeMonths = moment().subtract(3, 'months');
    if (!moment.isMoment(lastThreeMonths) || !moment.isMoment(day)) return false;

    const aYear = day.year();
    const aMonth = day.month();

    const bYear = lastThreeMonths.year();
    const bMonth = lastThreeMonths.month();

    const isSameYear = aYear === bYear;
    const isSameMonth = aMonth === bMonth;

    if (day <= moment()) {
      if (isSameYear && isSameMonth) return day.date() < lastThreeMonths.date();
      if (isSameYear) return aMonth < bMonth;
      return aYear < bYear;
    }

    return true;
  };

  /**
   * Convert data to a format readable by Recharts
   */
  convertData() {
    const { transactions } = this.props;
    const { startDate, endDate } = this.state;
    const finalData = [];
    const dateRange = TransactionGraph.getDateRange(startDate, endDate);

    dateRange.forEach((value) => {
      const formatedDate = moment(value).format(this.dateFormat);

      if (formatedDate in transactions) {
        finalData.push({
          ...transactions[formatedDate],
        });
      } else {
        finalData.push({
          total_amount: 0,
          total_buyer: 0,
          date: formatedDate,
        });
      }
    });

    return finalData;
  }

  /**
   * Render the graph
   */
  renderGraph() {
    const { startDate, endDate } = this.state;
    if (startDate && endDate) {
      const data = this.convertData();

      return (
        <div>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={data}>
              <XAxis dataKey="date" interval={10} />
              <YAxis yAxisId="left" hide />
              <YAxis yAxisId="right" orientation="right" hide />
              <Tooltip />
              <Bar
                yAxisId="right"
                type="monotone"
                dataKey="total_buyer"
                fill="#00A4AA"
                barSize={15}
                name="Jumlah pembeli"
              />
              <Line
                name="Jumlah pembelian"
                yAxisId="left"
                type="monotone"
                dataKey="total_amount"
                stroke="#B92160"
                activeDot={{ r: 8 }}
              />
              <Legend />
              <CartesianGrid strokeDasharray="3 3" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      );
    }

    return null;
  }

  /**
   * Render the component
   */
  render() {
    const { isLoading, isLoaded } = this.props;
    const { startDate, endDate, focusedInput } = this.state;
    const showLoading = isLoading ? 'show' : '';

    return (
      <div className={`form-wrapper content-wrapper ${showLoading}`}>
        <div className="form-wrapper__inner">
          <div className="entry-header">
            <h2 className="block-title">Grafik Riwayat Transaksi</h2>
          </div>

          <div className="mb-10">
            <DateRangePicker
              startDate={startDate} // momentPropTypes.momentObj or null,
              startDateId="DPstartDate" // PropTypes.string.isRequired,
              endDate={endDate} // momentPropTypes.momentObj or null,
              endDateId="DPendDate" // PropTypes.string.isRequired,
              onDatesChange={({ startDate: start, endDate: end }) => {
                this.setState({ startDate: start, endDate: end });
              }} // PropTypes.func.isRequired,
              focusedInput={focusedInput}
              onFocusChange={focusedInput => this.setState({ focusedInput })} // PropTypes.func.isRequired,
              numberOfMonths={1}
              isOutsideRange={this.isDayDisabled}
              small
              showClearDates
              noBorder
              showDefaultInputIcon
            />
          </div>

          <div className="mb-30">
            <button
              onClick={() => this.onRangePresetChanged('week')}
              type="button"
              className="button button--no-rounded button--small"
            >
              Minggu Ini
            </button>&nbsp;
            <button
              onClick={() => this.onRangePresetChanged('month')}
              type="button"
              className="button button--no-rounded button--small"
            >
              Bulan Ini
            </button>
          </div>

          <div className="mb-30">{isLoaded && this.renderGraph()}</div>

          <div className="text-medium text-color-gray-1 mb-30">
            Lebih suka melihat data dalam bentuk tabel?<br />
            <br />
            <Link className="button button--secondary button--small " to="/transactions">
              Lihat Tabel
            </Link>
          </div>
        </div>

        <LoadingOverlay />
      </div>
    );
  }
}

const mapStateToProps = ({ transactionGraph }) => ({
  ...transactionGraph,
});

export default connect(mapStateToProps)(TransactionGraph);



// WEBPACK FOOTER //
// ./src/pages/TransactionGraph/index.js